import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';

import useAuthStore from 'src/shared/store/authStore';
import { authApi } from './authApi';
import type {
  BusinessUpdatePayload,
  ChangePasswordPayload,
  LoginPayload,
  ProfileUpdatePayload,
} from './types';

export const useSession = () => useAuthStore((s) => s.user);
export const useIsAuthenticated = () => useAuthStore((s) => Boolean(s.accessToken));
/** Why the last session ended, for the login screen to say. */
export const useSignOutNotice = () => useAuthStore((s) => s.signOutNotice);

export const useLogin = () => {
  const setSession = useAuthStore((s) => s.setSession);
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: LoginPayload) => authApi.login(payload),
    onSuccess: (data) => {
      setSession({
        accessToken: data.accessToken,
        refreshToken: data.refreshToken,
        user: data.user,
      });
      // The cache may hold the previous user's data; a fresh session must not
      // render it for a moment before refetching.
      queryClient.clear();
      void navigate('/', { replace: true });
    },
  });
};

export const useLogout = () => {
  const clearSession = useAuthStore((s) => s.clearSession);
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      const { refreshToken } = useAuthStore.getState();
      if (refreshToken) {
        // Best effort: a failed revoke must not trap the user in a session
        // they asked to leave. The token expires on its own regardless.
        await authApi.logout(refreshToken).catch(() => undefined);
      }
    },
    onSettled: () => {
      clearSession();
      queryClient.clear();
      void navigate('/login', { replace: true });
    },
  });
};

/** Saves the user's own name; the shell's avatar and menu read it from the session. */
export const useUpdateProfile = () => {
  const setUser = useAuthStore((s) => s.setUser);
  return useMutation({
    mutationFn: (data: ProfileUpdatePayload) => authApi.updateMe(data),
    onSuccess: (user) => setUser(user),
  });
};

/**
 * Changes the user's own password, then signs her out here too.
 *
 * The API revokes every session, this one's included. Leaving now, with a word
 * on the login screen about why, beats being thrown out without one when the
 * access token next expires. No `/auth/logout`: there is nothing left to revoke.
 */
export const useChangePassword = () => {
  const clearSession = useAuthStore((s) => s.clearSession);
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: ChangePasswordPayload) => authApi.changePassword(data),
    onSuccess: () => {
      const email = useAuthStore.getState().user?.email ?? '';
      clearSession({ reason: 'passwordChanged', email });
      queryClient.clear();
      void navigate('/login', { replace: true });
    },
  });
};

/** The business: timezone and currency for every date and amount. */
export const useBusiness = () => {
  const isAuthenticated = useIsAuthenticated();
  return useQuery({
    queryKey: ['business'],
    queryFn: () => authApi.business(),
    enabled: isAuthenticated,
    staleTime: Infinity,
  });
};

/**
 * Changes the business settings. Every screen renders dates and money from
 * `['business']`, so the cache is replaced with the answer rather than merely
 * invalidated: the cutoff the finance forms check must be the new one at once.
 */
export const useUpdateBusiness = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: BusinessUpdatePayload) => authApi.updateBusiness(data),
    onSuccess: (business) => {
      queryClient.setQueryData(['business'], business);
    },
  });
};
